/**
 * Creator Promo — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the `/api/v1/creator-promo`
 * surface (backend feature OPUS-117). Mirrors the contract declared in
 * `apps/api/src/routes/creator-promo.ts`:
 *
 *   POST /api/v1/creator-promo                    — create a promo code.
 *   GET  /api/v1/creator-promo/by-influencer/:id  — list codes for an
 *                                                   influencer in the
 *                                                   current tenant.
 *   POST /api/v1/creator-promo/validate           — {code, influencer_id}
 *                                                   → {valid, reason?}.
 *   POST /api/v1/creator-promo/redeem             — {code, influencer_id}
 *                                                   → {redeemed, promo?,
 *                                                      reason?}.
 *
 * Keep this file React-free so the form module can import typed helpers
 * and the reducer without pulling any UI deps; a parallel test file can
 * exercise the reducer/validator directly.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Canonical promo kinds — mirrors the three variants the backend accepts.
// ---------------------------------------------------------------------------

export const PROMO_KINDS = ["exclusive", "general", "unique"] as const;
export type PromoKind = (typeof PROMO_KINDS)[number];

export const PROMO_KIND_LABELS: Record<PromoKind, string> = {
	exclusive: "Exclusive",
	general: "General",
	unique: "Unique",
};

/** Short operator-facing blurb shown under the radio group. */
export const PROMO_KIND_DESCRIPTIONS: Record<PromoKind, string> = {
	exclusive:
		"Only the issued influencer can redeem this code. Anyone else sees invalid_influencer.",
	general:
		"Any influencer inside this workspace can redeem. The influencer id stamps authorship.",
	unique:
		"Single-use across every workspace. Burned on first redemption. Max uses locked to 1.",
};

// ---------------------------------------------------------------------------
// API row shapes — the backend returns `active` as a boolean via `rowToView`.
// ---------------------------------------------------------------------------

export interface CreatorPromoCodeView {
	id: string;
	influencer_id: string;
	code: string;
	kind: PromoKind;
	max_uses: number | null;
	used_count: number;
	active: boolean;
	created_at: number;
}

export interface CreatePromoResponse {
	promo: CreatorPromoCodeView;
}

export interface ListByInfluencerResponse {
	influencer_id: string;
	codes: CreatorPromoCodeView[];
}

export interface ValidateResponse {
	valid: boolean;
	reason?: string;
}

export interface RedeemResponse {
	redeemed: boolean;
	reason?: string;
	promo?: CreatorPromoCodeView;
}

// ---------------------------------------------------------------------------
// Derived presentation helpers.
// ---------------------------------------------------------------------------

/** At-cap test: max_uses is set and used_count has hit it. */
export function isAtCap(row: CreatorPromoCodeView): boolean {
	return row.max_uses !== null && row.used_count >= row.max_uses;
}

/** Whether the code should render a "disabled" badge in the table. */
export function isDisabled(row: CreatorPromoCodeView): boolean {
	return !row.active || isAtCap(row);
}

/**
 * Map backend error codes (validate.reason / redeem.reason) to human-
 * readable copy. Keys mirror the pure `evaluateCode` reason set in the
 * API + the rare network fallback.
 */
export const REASON_COPY: Record<string, string> = {
	not_found: "No such code in this workspace.",
	inactive: "Code is inactive and cannot be redeemed.",
	max_uses_reached: "Code has already hit its redemption cap.",
	invalid_influencer:
		"Exclusive code — this influencer is not the original owner.",
	invalid_body: "Request was malformed. Double-check the inputs.",
	forbidden: "You need admin access to perform this action.",
	code_already_exists_global:
		"Unique code already taken in another workspace.",
	code_already_exists_in_tenant:
		"That code already exists for this workspace.",
};

export function reasonCopy(reason: string | undefined | null): string {
	if (!reason) return "Rejected by the server.";
	return REASON_COPY[reason] ?? `Rejected: ${reason}`;
}

// ---------------------------------------------------------------------------
// Form reducer — kept in api.ts so the test file can exercise it without
// JSX. The UI module imports the same helpers, so there is one source of
// truth for the unique-kind-locks-max-uses invariant.
// ---------------------------------------------------------------------------

export type DiscountUnit = "bps" | "pct";

export interface FormState {
	kind: PromoKind;
	code: string;
	influencerId: string;
	maxUses: string;
	discountBps: string;
	active: boolean;
}

export const EMPTY_FORM: FormState = {
	kind: "exclusive",
	code: "",
	influencerId: "",
	maxUses: "",
	discountBps: "",
	active: true,
};

export type FormAction =
	| { type: "set_kind"; kind: PromoKind }
	| { type: "set_code"; code: string }
	| { type: "set_influencer_id"; influencerId: string }
	| { type: "set_max_uses"; maxUses: string }
	| { type: "set_discount_bps"; discountBps: string }
	| { type: "set_active"; active: boolean }
	| { type: "reset" };

/**
 * Core form reducer. Invariants worth noting:
 *   - 'unique' kind always forces max_uses = "1" and locks the field in
 *     the UI (the reducer normalises; the UI reads `isMaxUsesLocked`).
 *   - Switching to 'unique' overwrites whatever was in maxUses.
 *   - Switching away from 'unique' clears maxUses so the operator sets
 *     a fresh cap instead of inheriting the forced "1".
 */
export function formReducer(state: FormState, action: FormAction): FormState {
	switch (action.type) {
		case "set_kind": {
			if (action.kind === "unique") {
				return {
					...state,
					kind: "unique",
					maxUses: "1",
					// Unique codes can live without a specific influencer owner in
					// spec, but we keep whatever the operator typed — the server
					// still records the authoring influencer.
				};
			}
			if (state.kind === "unique") {
				// Leaving unique — clear the forced "1" so the operator sets
				// a fresh cap for the new kind.
				return { ...state, kind: action.kind, maxUses: "" };
			}
			return { ...state, kind: action.kind };
		}
		case "set_code":
			return { ...state, code: action.code };
		case "set_influencer_id":
			return { ...state, influencerId: action.influencerId };
		case "set_max_uses":
			// If unique, ignore attempts to change maxUses.
			if (state.kind === "unique") return { ...state, maxUses: "1" };
			return { ...state, maxUses: action.maxUses };
		case "set_discount_bps":
			return { ...state, discountBps: action.discountBps };
		case "set_active":
			return { ...state, active: action.active };
		case "reset":
			return { ...EMPTY_FORM };
		default:
			return state;
	}
}

/** Is the max_uses input locked by the current kind? */
export function isMaxUsesLocked(kind: PromoKind): boolean {
	return kind === "unique";
}

// ---------------------------------------------------------------------------
// Validation — hand-rolled schema mirroring Zod's `.safeParse` surface. We
// stay dep-free on zod so the lane only touches owned files.
// ---------------------------------------------------------------------------

const CODE_REGEX = /^[A-Za-z0-9_-]+$/;
const CODE_MIN = 3;
const CODE_MAX = 64;
const ID_MAX = 120;
const DISCOUNT_BPS_MAX = 10_000;

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface ParsedCreateInput {
	kind: PromoKind;
	code: string;
	influencerId: string;
	maxUses: number | null;
	discountBps: number | null;
	active: boolean;
}

export function validateFormState(state: FormState): ParseResult<ParsedCreateInput> {
	const errors: Record<string, string> = {};

	const code = state.code.trim();
	if (code.length < CODE_MIN || code.length > CODE_MAX) {
		errors.code = `Code must be ${CODE_MIN}–${CODE_MAX} characters.`;
	} else if (!CODE_REGEX.test(code)) {
		errors.code = "Use letters, digits, dashes, or underscores only.";
	}

	const influencerId = state.influencerId.trim();
	if (state.kind === "exclusive") {
		if (influencerId.length === 0) {
			errors.influencerId = "Influencer ID is required for exclusive codes.";
		} else if (influencerId.length > ID_MAX) {
			errors.influencerId = `Influencer ID must be ${ID_MAX} chars or fewer.`;
		}
	} else if (state.kind === "general") {
		if (influencerId.length > ID_MAX) {
			errors.influencerId = `Influencer ID must be ${ID_MAX} chars or fewer.`;
		}
	}
	// 'unique' — influencer_id still recorded as author, but empty is OK.

	let maxUses: number | null = null;
	if (state.kind === "unique") {
		maxUses = 1;
	} else {
		const raw = state.maxUses.trim();
		if (raw.length > 0) {
			const n = Number(raw);
			if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
				errors.maxUses = "Max uses must be a positive integer.";
			} else {
				maxUses = n;
			}
		}
	}

	let discountBps: number | null = null;
	const dRaw = state.discountBps.trim();
	if (dRaw.length > 0) {
		const n = Number(dRaw);
		if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > DISCOUNT_BPS_MAX) {
			errors.discountBps = `Discount must be an integer between 0 and ${DISCOUNT_BPS_MAX} bps.`;
		} else {
			discountBps = n;
		}
	}

	if (Object.keys(errors).length > 0) return { success: false, errors };
	return {
		success: true,
		data: {
			kind: state.kind,
			code,
			// For 'unique', influencer_id is optional on the server; emit an
			// empty-string-safe fallback so the body stays valid if the operator
			// left it blank. The backend requires a non-empty string, so we
			// stamp the authoring user with a synthetic "creator_unknown" to
			// keep the row insertable while telling the story truthfully.
			influencerId:
				state.kind === "unique" && influencerId.length === 0
					? "creator_unknown"
					: influencerId,
			maxUses,
			discountBps,
			active: state.active,
		},
	};
}

// ---------------------------------------------------------------------------
// Remote operations — TanStack Query consumers import these directly.
// ---------------------------------------------------------------------------

const BASE = "/creator-promo";

export interface CreatePromoArgs {
	kind: PromoKind;
	code: string;
	influencerId: string;
	maxUses: number | null;
	active: boolean;
	/** Discount is accepted for forward-compat; server currently ignores it. */
	discountBps?: number | null;
}

export async function createPromo(
	args: CreatePromoArgs,
): Promise<CreatorPromoCodeView> {
	try {
		const res = await apiClient.post<CreatePromoResponse>(BASE, {
			kind: args.kind,
			code: args.code,
			influencerId: args.influencerId,
			maxUses: args.maxUses,
			active: args.active,
			discountBps: args.discountBps ?? null,
		});
		return res.promo;
	} catch (err) {
		// Normalise the two known server-side conflicts to stable reason keys
		// so the UI can map through `REASON_COPY` without duplicating strings.
		if (err instanceof ApiError && err.status === 409) {
			const body = err.body as { error?: string } | null;
			if (body?.error) {
				throw new ApiError(409, reasonCopy(body.error), err.body);
			}
		}
		throw err;
	}
}

export async function listPromosByInfluencer(
	influencerId: string,
): Promise<CreatorPromoCodeView[]> {
	try {
		const res = await apiClient.get<ListByInfluencerResponse>(
			`${BASE}/by-influencer/${encodeURIComponent(influencerId)}`,
		);
		return res.codes;
	} catch (err) {
		// 404 is unusual here (the server always returns an empty list) — but
		// treat it as "no codes" for UI ergonomics.
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export interface ValidateArgs {
	code: string;
	influencerId: string;
}

export async function validatePromo(args: ValidateArgs): Promise<ValidateResponse> {
	return apiClient.post<ValidateResponse>(`${BASE}/validate`, {
		code: args.code,
		influencer_id: args.influencerId,
	});
}

export async function redeemPromo(args: ValidateArgs): Promise<RedeemResponse> {
	try {
		return await apiClient.post<RedeemResponse>(`${BASE}/redeem`, {
			code: args.code,
			influencer_id: args.influencerId,
		});
	} catch (err) {
		if (err instanceof ApiError && err.status === 409) {
			const body = err.body as RedeemResponse | null;
			if (body && body.redeemed === false) return body;
		}
		if (err instanceof ApiError && err.status === 403) {
			return { redeemed: false, reason: "forbidden" };
		}
		throw err;
	}
}

// ---------------------------------------------------------------------------
// Query keys — stable shape for TanStack Query cache invalidation.
// ---------------------------------------------------------------------------

export const creatorPromoQueryKeys = {
	all: ["creator-promo"] as const,
	byInfluencer: (influencerId: string) =>
		[...creatorPromoQueryKeys.all, "by-influencer", influencerId] as const,
};
